<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>
    <b-card>
      <b-overlay :show="showLoading" rounded="sm">
        <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
          <h4 class="col-12 col-xl-2 mb-1 mb-xl-0 font-weight-bold">
            分店庫存管理
          </h4>
          <div
            class="
              col-12 col-xl-10
              d-flex
              flex-column flex-xl-row
              align-items-start align-items-xl-center justify-content-xl-end
            "
          >
            <b-button
              v-b-toggle.collapse-advanced-search
              variant="outline-info"
              class="mb-2 mb-xl-0"
              style="height: 34px;"
            >
              <span class="mdi mdi-magnify mt-1 mr-1"></span>進階搜尋
            </b-button>
          </div>
          <div class="col-12 mt-2">
            <b-collapse id="collapse-advanced-search" v-model="isAdvancedSearchVisible">
              <b-card>
                <b-form>
                  <div class="row mb-2">
                    <div class="col-12 col-xl-6 mb-2">
                      <div class="d-flex align-items-center">
                        <label class="filter-label mb-0 mr-2">分店</label>
                        <AppSingleSelect
                          id="branch-select"
                          class="advanced-search-control flex-grow-1"
                          v-model="selectedBranch"
                          :options="branchOptions"
                          :disabled="isBranchSelectDisabled"
                        />
                      </div>
                    </div>
                    <div class="col-12 col-xl-6 mb-2">
                      <div class="d-flex align-items-center">
                        <label class="filter-label mb-0 mr-2">類型</label>
                        <b-form-select
                          v-model="query.type"
                          class="advanced-search-control flex-grow-1"
                        >
                          <template #first>
                            <b-form-select-option :value="null">全部類型</b-form-select-option>
                          </template>
                          <b-form-select-option
                            v-for="productType in productTypes"
                            :key="productType.value"
                            :value="productType.value"
                          >
                            {{ productType.text }}
                          </b-form-select-option>
                        </b-form-select>
                      </div>
                    </div>
                    <div class="col-12 col-xl-6 mb-2">
                      <div class="d-flex align-items-center">
                        <label class="filter-label mb-0 mr-2">分類</label>
                        <b-form-select
                          v-model="query.category"
                          :options="productCategoryOptions"
                          class="advanced-search-control flex-grow-1"
                        ></b-form-select>
                      </div>
                    </div>
                    <div class="col-12 col-xl-6 mb-2">
                      <div class="d-flex align-items-center">
                        <label class="filter-label mb-0 mr-2">狀態</label>
                        <b-form-select
                          v-model="query.is_enabled"
                          :options="productStatusOptions"
                          class="advanced-search-control flex-grow-1"
                        ></b-form-select>
                      </div>
                    </div>
                    <div class="col-12 col-xl-6 mb-2">
                      <div class="d-flex align-items-center">
                        <label class="filter-label mb-0 mr-2">通路</label>
                        <b-form-select
                          v-model="query.merchant_id"
                          :options="merchants"
                          :disabled="merchants.length == 2"
                          class="advanced-search-control flex-grow-1"
                        ></b-form-select>
                      </div>
                    </div>
                  </div>
                  <div class="d-flex">
                    <b-button
                      variant="primary"
                      class="mr-2"
                      @click="resetAdvancedSearch"
                    >
                      <i class="mdi mdi-restart"></i> 重置條件
                    </b-button>
                    <b-button
                      variant="primary"
                      @click="handleSearchFromAdvanced"
                    >
                      <i class="fa fa-search"></i> 搜尋
                    </b-button>
                  </div>
                </b-form>
              </b-card>
            </b-collapse>
          </div>
          <div class="col-12 mt-2">
            <b-card class="mb-0">
              <b-input-group>
                <b-form-input
                  placeholder="請輸入商品名稱或商品序號"
                  v-model="query.keyword"
                  v-on:keyup.enter="handlePageReset"
                ></b-form-input>
                <b-input-group-append>
                  <b-button @click="handlePageReset">
                    <i class="fa fa-search"></i>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-card>
          </div>
        </div>
        <b-table responsive striped hover :items="productSkus" :fields="fields">
          <template #cell(name)="data">
            <router-link
              :to="{
                name: 'ProductDetail',
                params: {
                  product_id: data.item.product.id,
                },
              }"
            >
              <div class="mb-1">
                <span
                  :id="`branch-stock-product-title-${data.item.id}`"
                >
                  {{
                    hiddenString(
                      data.item.product.title === data.item.sku_name
                        ? data.item.product.title
                        : data.item.product.title + " " + data.item.sku_name,
                      50
                    )
                  }}
                </span>
                <b-button
                  v-if="hasConfigValue(data.item.config)"
                  class="config-link ml-1"
                  @click.prevent.stop="openConfigModal(data.item)"
                  title="查看 product_skus.config"
                >
                  <i class="mdi mdi-arrow-expand-all"></i>
                </b-button>
              </div>
              <b-popover
                v-if="data.item.product.title || data.item.sku_name"
                :target="`branch-stock-product-title-${data.item.id}`"
                triggers="hover focus"
                placement="right"
              >
                <div class="text-left">
                  {{
                    data.item.product.title === data.item.sku_name
                      ? data.item.product.title
                      : data.item.product.title + " " + data.item.sku_name
                  }}
                </div>
              </b-popover>
              <div :id="`branch-stock-sku-code-${data.item.id}`">
                {{ hiddenString(`${data.item.sku_code || ""} ${data.item.sku_outer_code || ""}`.trim(), 50) }}
              </div>
              <b-popover
                v-if="data.item.sku_code || data.item.sku_outer_code"
                :target="`branch-stock-sku-code-${data.item.id}`"
                triggers="hover focus"
                placement="right"
              >
                <div class="text-left">{{ `${data.item.sku_code || ""} ${data.item.sku_outer_code || ""}`.trim() }}</div>
              </b-popover>
            </router-link>
          </template>
          <template #cell(picture)="data">
            <b-img-lazy
              v-if="
                data.item.product.images[0] &&
                data.item.product.images[0].pic_url
              "
              class="thumb-image"
              :src="
                data.item.product.images[0] &&
                data.item.product.images[0].pic_url
              "
              alt="商品圖片"
              width="50"
              height="50"
            />
          </template>
          <template #cell(max_quantity)="data">
            <div v-if="data.item.isEditing">
              <b-form-input style="max-width:125px;" v-model="data.item.max_quantity" type="number" min="0"/>
            </div>
            <div v-else>
              {{ data.item.max_quantity }}
            </div>
          </template>
          <template #cell(quantity)="data">
            <div v-if="data.item.isEditing">
              <b-form-input style="max-width:125px;" v-model="data.item.quantity" type="number" min="0"/>
            </div>
            <div v-else>
              {{ data.item.quantity }}
            </div>
          </template>
          <template #cell(sold_quantity)="data">
            <div class="mb-1">
              {{ data.item.max_quantity - data.item.quantity }}
            </div>
          </template>
          <template #cell(arrange)="data">
            <b-button
              class="ml-2"
              variant="inverse-warning"
              v-if="checkPermission([consts.BRANCH_PRODUCT_STOCK_MODIFY, consts.BRANCH_PRODUCT_STOCK_MODIFY_NO_BRANCH])"
              @click="
                data.item.isEditing
                  ? updateProductSkuMaxQuantity(
                      data.item,
                      data.item.max_quantity,
                      data.item.quantity
                    )
                  : toggleEditMode(data.item)
              "
            >
              <span class="mdi mdi-lead-pencil mr-1"></span>
              {{ data.item.isEditing ? "更新" : "編輯" }}
            </b-button>
          </template>
        </b-table>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-overlay>
    </b-card>
    <b-modal
      id="branch-product-stock-config-modal"
      :title="configModalTitle"
      size="lg"
      hide-footer
    >
      <pre class="config-preview mb-0">{{ configModalContent }}</pre>
    </b-modal>
  </div>
</template>

<script>
import productApi from "../../../apis/product";
import merchantApi from "../../../apis/merchant";
import PermissionChecker from "@/utils/PermissionChecker";
import * as consts from "@/consts";
import {updateQueryFromRoute, updateUrl, updateUrlWithoutReload} from "@/utils/updateUrl";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { mapState, mapActions } from "vuex";
import AppSingleSelect from "@/components/AppSingleSelect.vue";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: {AppSingleSelect, CustomPagination, BreadCrumb },
  data() {
    return {
      consts,
      showLoading: false,
      isAdvancedSearchVisible: false,
      totalRows: 0,
      fields: [
        {
          key: "name",
          label: "商品名稱/編號",
        },
        {
          key: "picture",
          label: "商品圖片",
        },
        {
          key: "max_quantity",
          label: "原始總量",
        },
        {
          key: "quantity",
          label: "可售庫存",
        },
        {
          key: "sold_quantity",
          label: "已售出數量",
        },
        {
          key: "arrange",
          label: "管理",
        },
      ],
      permissionChecker: new PermissionChecker(),
      initialized: false,
      query: {
        page: 1,
        per_page: 10,
        keyword: "",
        branch_id: null,
        merchant_id: null,
        type: null,
        category: '*',
        is_enabled: 1,
      },
      productSkus: [],
      selectedBranch: null,
      merchants: [{ value: null, text: "請選擇" }],
      productTypes: [],
      productCategories: [],
      productStatusOptions: [
        { value: null, text: '全部' },
        { value: 1, text: '啟用中' },
        { value: 0, text: '已停用' },
      ],
      configModalTitle: "product_skus.config",
      configModalContent: "",
    };
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.fetchSkus();
    },
  },
  computed: {
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
    ...mapState("general", ["myBranches", "currentBranch"]),
    canModifyBranchStock() {
      return this.checkPermission([consts.BRANCH_PRODUCT_STOCK_MODIFY]);
    },
    canModifyBranchStockNoBranch() {
      return this.checkPermission([consts.BRANCH_PRODUCT_STOCK_MODIFY_NO_BRANCH]);
    },
    isBranchSelectDisabled() {
      return this.canModifyBranchStockNoBranch && !this.canModifyBranchStock;
    },
    branchOptions() {
      let options = [];

      if (this.canModifyBranchStockNoBranch) {
        options.push({
          value: "null",
          text: "品牌組織階層(無隸屬分店)",
        });
      }

      if (this.canModifyBranchStock || options.length === 0) {
        options = [
          ...options,
          ...this.myBranches.map(branch => {
            let branchName = `${branch.branch_code} ${branch.name}`
            return {
              value: branch.id,
              text: branchName
            }
          }),
        ];
      }

      return options;
    },
    productCategoryOptions() {
      return [
        { value: '*', text: '全部分類' },
        { value: 'null', text: '未分類' },
        ...this.productCategories.map(category => ({ value: category, text: category }))
      ]
    },
  },
  async created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await Promise.all([
      this.fetchMerchants(),
      this.fetchProductTypes(),
      this.fetchProductCategories(),
    ]);
    await this.fetchSkus();
    this.initialized = true;

    if (this.query.branch_id !== null && this.query.branch_id !== undefined) {
      this.selectedBranch = this.branchOptions.find(branch => branch.value === this.query.branch_id);
    } else if (this.currentBranch) {
      this.selectedBranch = this.branchOptions.find(branch => branch.value === this.currentBranch.id);
    } else if (this.canModifyBranchStockNoBranch) {
      this.selectedBranch = this.branchOptions.find(branch => branch.value === "null");
    }
    this.query.branch_id = this.selectedBranch?.value ?? null;
  },
  methods: {
    ...mapActions("general", ["setCurrentBranch"]),

    async fetchProductTypes() {
      try {
        const { data } = await productApi.getProductTypes();
        this.productTypes = data.data.map(type => ({ value: type.value, text: type.text }));
      } catch (error) {
        console.error(error);
        alert("取得商品類型錯誤");
      }
    },
    async fetchProductCategories() {
      try {
        const { data } = await productApi.getProductCategories();
        this.productCategories = data.data ?? [];
      } catch (error) {
        console.error(error);
        alert("取得商品分類錯誤");
      }
    },
    async fetchMerchants() {
      const { data } = await merchantApi.list();

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: `${merchant.name} (${merchant.type_name})`,
          };
        }),
      ];

      let defaultMerchant = this.findDefaultMerchant(data.data);

      if (defaultMerchant) {
        this.query.merchant_id = defaultMerchant.id;
      }
    },
    findDefaultMerchant(merchants) {
      if (merchants.length == 1) {
        return merchants[0];
      }
      return (
        merchants.find(
          (merchant) => merchant.type === this.defaultMerchantType
        ) ?? merchants.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE)
      );
    },
    async fetchSkus() {
      const branchId = this.normalizeBranchIdForPayload(this.query.branch_id);

      if (!branchId && !this.canModifyBranchStockNoBranch && this.currentBranch) {
        this.query.branch_id = this.currentBranch.id;
        this.selectedBranch = this.branchOptions.find(branch => branch.value === this.currentBranch.id);
      }

      this.showLoading = true;

      try {
        const params = {
          ...this.query,
          branch_id: this.normalizeBranchIdForPayload(this.query.branch_id),
        };
        if (this.query.category === '*') {
          delete params.category;
        }

        const { data } = await productApi.getSkus(params);

        this.skus = data.data;
        this.totalRows = data.meta.total;

        this.productSkus = this.skus.map((sku) => {
          return {
            ...sku,
            max_quantity: sku.max_quantity ?? 0,
            quantity: sku.quantity ?? 0,
            max_quantity_orig: sku.max_quantity ?? 0,
            quantity_orig: sku.quantity ?? 0,
          };
        });
      } catch (error) {
        console.error(error);
        alert("取得商品資料錯誤");
      }
      this.showLoading = false;
    },
    checkPermission(permissions) {
      return this.permissionChecker.checkAny(permissions);
    },
    handlePageChange(newPage) {
      this.query.page = newPage;
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
    },
    handlePageReset() {
      this.syncBranchFilterToQuery();
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }
      this.fetchSkus();
    },
    handleBranchChange() {
      this.syncBranchFilterToQuery();
      this.query.page = 1; // Reset to the first page when branch changes
      this.$nextTick(() => {
        this.handlePageReset();
      });
    },
    syncBranchFilterToQuery() {
      this.query.branch_id = this.selectedBranch?.value ?? null;
    },
    normalizeBranchIdForPayload(branchId) {
      return branchId === "null" ? null : branchId;
    },
    handleSearchFromAdvanced() {
      this.handleBranchChange();
    },
    resetAdvancedSearch() {
      this.selectedBranch = this.branchOptions[0] ?? null;
      this.query.type = null;
      this.query.category = "*";
      this.query.is_enabled = 1;

      if (this.merchants.length === 2) {
        const defaultMerchant = this.merchants.find((merchant) => merchant.value !== null);
        this.query.merchant_id = defaultMerchant?.value ?? null;
      } else {
        this.query.merchant_id = null;
      }

      this.handleSearchFromAdvanced();
    },
    toggleEditMode(row) {
      this.$set(row, "isEditing", !row.isEditing);
    },
    hiddenString(value, length) {
      value = value || "";
      const splittedValue = [...value];
      if (splittedValue.length <= length) {
        return value;
      }
      return splittedValue.slice(0, length).join("") + "...";
    },
    hasConfigValue(config) {
      if (config === null || config === undefined) {
        return false;
      }
      if (typeof config === "string") {
        return config.trim().length > 0;
      }
      if (Array.isArray(config)) {
        return config.length > 0;
      }
      if (typeof config === "object") {
        return Object.keys(config).length > 0;
      }
      return true;
    },
    formatConfig(config) {
      if (typeof config === "string") {
        const trimmedConfig = config.trim();
        if (!trimmedConfig) {
          return "";
        }
        try {
          return JSON.stringify(JSON.parse(trimmedConfig), null, 2);
        } catch (error) {
          return trimmedConfig;
        }
      }
      if (typeof config === "object") {
        return JSON.stringify(config, null, 2);
      }
      return String(config ?? "");
    },
    openConfigModal(row) {
      this.configModalTitle = row?.product?.title || "product_skus.config";
      this.configModalContent = this.formatConfig(row?.config);
      this.$bvModal.show("branch-product-stock-config-modal");
    },
    async updateProductSkuMaxQuantity(row, max_quantity, quantity) {
      const result = await this.$swal.fire({
        title: "確認修改",
        html: `
          <p>
            將『${row.product.title} / ${row.sku_name}』
          </p>
          <p>
            原始總量從 ${row.max_quantity_orig} 調整成 ${max_quantity}
          </p>
          <p>
            可售庫存從 ${row.quantity_orig} 調整成 ${quantity} 嗎？
          </p>
        `,
        showCancelButton: true,
        confirmButtonText: "確認修改",
        cancelButtonText: "取消",
      });

      if (result.value) {
        try {
          const organizationId = this.$store.state.general.organization.id;
          await productApi.updateProductSkuMaxQuantity(organizationId, row.id, {
            max_quantity,
            quantity,
          });

          row.max_quantity_orig = max_quantity;
          row.quantity_orig = quantity;
          this.$set(row, "isEditing", false);

          this.$swal.fire({
            title: "成功",
            type: "success",
            text: "更新成功",
          });
        } catch (error) {
          this.$swal("失敗", error.response.data.message, "error");
        }
      } else {
        row.max_quantity = row.max_quantity_orig;
        row.quantity = row.quantity_orig;
        this.$set(row, "isEditing", false);
      }
    },
  },
};
</script>

<style>
.modal-backdrop {
  opacity: 0.5;
}

.advanced-search-control {
  height: 36px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.filter-label {
  min-width: 56px;
}

.config-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
  width: fit-content;
}

.config-preview {
  max-height: 60vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
}
</style>
